/**
 * Inner & loungewear silhouettes: camisole, pyjama_set, nightwear, robe,
 * thermal_set.
 *
 * boxer and brief live in bottoms.ts.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { placketButtons, wrapBelt, neckLabel, elasticGather, drawcords } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Camisole (spaghetti straps, V-neck, hip length) ─────────────── */
export function camisoleFlat(view: FlatView): string {
  const neckDip = view === "front" ? 136 : 100;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 168 68 L 166 80 Q 164 90 156 96 Q 136 112 136 136 L 136 390 Q 200 402 264 390 L 264 136 Q 264 112 244 96 Q 236 90 234 80 L 232 68 Q 200 ${neckDip - 40} 168 68 Z"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 168 68 Q 200 ${neckDip} 232 68"/>
  <path ${STITCH_ATTRS} d="M 170 72 Q 200 ${neckDip + 4} 230 72"/>
  <path ${DETAIL_ATTRS} d="M 168 68 L 168 80 Q 166 88 160 92"/>
  <path ${DETAIL_ATTRS} d="M 232 68 L 232 80 Q 234 88 240 92"/>`
      : `<path ${DETAIL_ATTRS} d="M 168 68 Q 200 ${neckDip} 232 68"/>
  <path ${STITCH_ATTRS} d="M 170 72 Q 200 ${neckDip + 4} 230 72"/>
  <path ${DETAIL_ATTRS} d="M 168 68 L 168 80 Q 166 88 160 92"/>
  <path ${DETAIL_ATTRS} d="M 232 68 L 232 80 Q 234 88 240 92"/>
  ${neckLabel(200, 68)}`
  }
  <path ${STITCH_ATTRS} d="M 137 380 Q 200 392 263 380"/>
  <path ${STITCH_ATTRS} d="M 136 386 Q 200 398 264 386"/>
</g>`;
}

/* ── Pyjama set (camp-collar shirt + full-length pants, side by side) ── */
export function pyjama_setFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 44 58 L 6 88 L 12 132 L 36 120 L 38 318 Q 100 328 162 318 L 164 120 L 188 132 L 194 88 L 156 58 Q 100 42 44 58 Z"/>
  <path ${DETAIL_ATTRS} d="M 14 96 L 18 124"/>
  <path ${DETAIL_ATTRS} d="M 182 124 L 186 96"/>
  <path ${STITCH_ATTRS} d="M 18 98 L 22 122"/>
  <path ${STITCH_ATTRS} d="M 178 122 L 182 98"/>
  <path ${STITCH_ATTRS} d="M 44 60 Q 40 88 36 120"/>
  <path ${STITCH_ATTRS} d="M 156 60 Q 160 88 164 120"/>
  <path ${STITCH_ATTRS} d="M 40 312 Q 100 322 160 312"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 96 46 L 40 54 L 36 78 L 88 62 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 104 46 L 160 54 L 164 78 L 112 62 Z"/>
  <path ${STITCH_ATTRS} d="M 92 50 L 44 58 L 42 72"/>
  <path ${STITCH_ATTRS} d="M 108 50 L 156 58 L 158 72"/>
  <path ${DETAIL_ATTRS} d="M 96 58 L 96 316"/>
  <path ${DETAIL_ATTRS} d="M 104 58 L 104 316"/>
  <path ${STITCH_ATTRS} d="M 98 62 L 98 314"/>
  ${placketButtons(100, [90, 130, 170, 210, 250])}
  <rect x="116" y="140" width="28" height="28" rx="2" ${DETAIL_ATTRS}/>
  <path ${STITCH_ATTRS} d="M 120 144 L 140 144 L 140 164 L 120 164 Z"/>`
      : `<path ${DETAIL_ATTRS} d="M 48 56 Q 100 44 152 56"/>
  <path ${STITCH_ATTRS} d="M 46 84 L 154 84"/>
  ${neckLabel(100, 48, 14, 10)}`
  }
  <path ${DETAIL_ATTRS} d="M 38 304 L 38 318"/>
  <path ${DETAIL_ATTRS} d="M 162 304 L 162 318"/>
  <path ${OUTLINE_ATTRS} d="M 248 50 L 362 50 L 366 70 L 368 214 L 358 458 L 316 458 L 314 220 Q 305 202 296 220 L 294 458 L 252 458 L 242 214 L 244 70 Z"/>
  <path ${DETAIL_ATTRS} d="M 244 70 L 366 70"/>
  <path ${STITCH_ATTRS} d="M 246 66 L 364 66"/>
  ${elasticGather(56, 250, 360)}
  ${
    view === "front"
      ? `${drawcords(305, 58)}`
      : `<path ${DETAIL_ATTRS} d="M 248 106 Q 305 120 362 106"/>
  <path ${STITCH_ATTRS} d="M 250 110 Q 305 124 360 110"/>`
  }
  <path ${STITCH_ATTRS} d="M 254 452 L 292 452"/>
  <path ${STITCH_ATTRS} d="M 318 452 L 356 452"/>
</g>`;
}

/* ── Nightwear (long flowing nightgown/shirt, loose fit) ──────────── */
export function nightwearFlat(view: FlatView): string {
  const neckDip = view === "front" ? 118 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 82 L 114 98 L 80 186 L 66 268 L 102 278 L 116 216 L 124 170 L 114 450 Q 200 462 286 450 L 276 170 L 284 216 L 298 278 L 334 268 L 320 186 L 286 98 L 242 82 Q 200 ${neckDip - 22} 158 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 158 82 Q 200 ${neckDip} 242 82"/>
  <path ${STITCH_ATTRS} d="M 161 87 Q 200 ${neckDip + 6} 239 87"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 66 268 L 102 278 L 98 296 L 62 286 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 334 268 L 298 278 L 302 296 L 338 286 Z"/>
  <path ${STITCH_ATTRS} d="M 114 100 Q 122 134 124 170"/>
  <path ${STITCH_ATTRS} d="M 286 100 Q 278 134 276 170"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 194 96 L 192 448"/>
  <path ${DETAIL_ATTRS} d="M 206 96 L 208 448"/>
  <path ${STITCH_ATTRS} d="M 198 100 L 196 446"/>
  <path ${STITCH_ATTRS} d="M 202 100 L 204 446"/>
  ${placketButtons(200, [130, 170, 210, 250, 290, 330])}`
      : `<path ${STITCH_ATTRS} d="M 132 130 L 268 130"/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 116 440 Q 200 452 284 440"/>
  <path ${STITCH_ATTRS} d="M 114 446 Q 200 458 286 446"/>
</g>`;
}

/* ── Robe (wrap front, shawl collar, belt, floor-length) ──────────── */
export function robeFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 82 L 110 98 L 72 196 L 54 296 L 90 306 L 108 230 L 120 174 L 108 458 Q 200 470 292 458 L 280 174 L 292 230 L 310 306 L 346 296 L 328 196 L 290 98 L 242 82 Q 200 70 158 82 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 54 296 L 90 306 L 86 326 L 50 316 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 346 296 L 310 306 L 314 326 L 350 316 Z"/>
  <path ${STITCH_ATTRS} d="M 110 100 Q 118 136 120 174"/>
  <path ${STITCH_ATTRS} d="M 290 100 Q 282 136 280 174"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 166 82 L 174 102 L 180 300"/>
  <path ${DETAIL_ATTRS} d="M 234 82 L 226 102 L 220 300"/>
  <path ${STITCH_ATTRS} d="M 170 86 L 178 106 L 184 296"/>
  <path ${STITCH_ATTRS} d="M 230 86 L 222 106 L 216 296"/>
  ${wrapBelt(200, 300, 72)}`
      : `<path ${DETAIL_ATTRS} d="M 162 82 Q 200 72 238 82"/>
  <path ${STITCH_ATTRS} d="M 126 124 L 274 124"/>
  ${neckLabel(200, 82)}
  ${wrapBelt(200, 300, 72)}`
  }
  <path ${STITCH_ATTRS} d="M 110 448 Q 200 460 290 448"/>
  <path ${STITCH_ATTRS} d="M 108 454 Q 200 466 292 454"/>
</g>`;
}

/* ── Thermal set (fitted top + fitted pants side by side) ─────────── */
export function thermal_setFlat(view: FlatView): string {
  const neckDip = view === "front" ? 68 : 56;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 44 46 L 22 54 L 10 106 L 4 152 L 28 158 L 36 130 L 42 100 L 42 228 L 170 228 L 170 100 L 176 130 L 184 158 L 208 152 L 202 106 L 190 54 L 168 46 Q 105 ${neckDip - 16} 44 46 Z"/>
  <path ${DETAIL_ATTRS} d="M 44 46 Q 105 ${neckDip} 168 46"/>
  <path ${STITCH_ATTRS} d="M 47 51 Q 105 ${neckDip + 4} 165 51"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 4 152 L 28 158 L 26 170 L 2 164 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 208 152 L 184 158 L 186 170 L 210 164 Z"/>
  <path ${STITCH_ATTRS} d="M 22 56 Q 34 78 42 100"/>
  <path ${STITCH_ATTRS} d="M 190 56 Q 178 78 170 100"/>
  ${
    view === "front"
      ? `<rect x="92" y="52" width="26" height="10" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 50 76 L 162 76"/>
  ${neckLabel(105, 46, 16, 10)}`
  }
  <path ${STITCH_ATTRS} d="M 44 220 Q 105 228 166 220"/>
  <path ${OUTLINE_ATTRS} d="M 236 46 L 360 46 L 364 68 L 372 268 L 332 268 L 324 148 Q 318 136 312 148 L 304 268 L 264 268 L 272 68 Z"/>
  ${elasticGather(56, 242, 354)}
  <path ${STITCH_ATTRS} d="M 274 64 L 362 64"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 318 68 L 318 134"/>
  <path ${STITCH_ATTRS} d="M 315 70 Q 312 100 314 136"/>`
      : `<path ${STITCH_ATTRS} d="M 276 96 Q 318 108 360 96"/>`
  }
  <path ${STITCH_ATTRS} d="M 266 260 L 302 260"/>
  <path ${STITCH_ATTRS} d="M 334 260 L 370 260"/>
</g>`;
}
